if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.initPdpVideoScrollAutoplay();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.kickAutoplayForActiveSlide());
        });

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 150));
        }

        this.syncGalleryLightboxMediaId();

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        if (this.pdpVideoObserver) {
          this.pdpVideoObserver.disconnect();
          this.pdpVideoObserver = null;
        }
      }

      initPdpVideoScrollAutoplay() {
        if (!this.elements.viewer || this.pdpVideoObserver) return;

        const scrollRoot =
          this.elements.viewer.slider instanceof Element ? this.elements.viewer.slider : null;

        const slideVisibleEnough = (entry) => {
          if (!entry.isIntersecting) return false;
          const w = entry.intersectionRect.width;
          const h = entry.intersectionRect.height;
          const rootH = entry.rootBounds?.height ?? 0;
          const minRatio = scrollRoot ? 0.22 : 0.3;
          return (
            (w >= 64 && h >= 64) ||
            entry.intersectionRatio >= minRatio ||
            (rootH > 0 && h >= rootH * 0.12)
          );
        };

        this.pdpVideoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const deferredMedia = entry.target.querySelector('deferred-media');
              if (!deferredMedia) return;
              if (slideVisibleEnough(entry)) {
                deferredMedia.loadContent(false, true, true);
              }
            });
          },
          {
            root: scrollRoot,
            rootMargin: '0px 8px',
            threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.35, 0.5, 0.75, 1],
          },
        );

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((slide) => {
          if (!slide.querySelector('deferred-media')) return;
          this.pdpVideoObserver.observe(slide);
        });
      }

      kickAutoplayForActiveSlide() {
        if (!this.elements.viewer) return;
        const active = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (active) this.playActiveMedia(active);
      }

      onSlideChanged(event) {
        const current = event.detail.currentElement;
        if (!current || !current.dataset.mediaId) return;

        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((el) => {
          el.classList.toggle('is-active', el === current);
        });

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }

        this.playActiveMedia(current);
        this.syncGalleryLightboxMediaId();
      }

      syncGalleryLightboxMediaId() {
        const root = this.querySelector('[data-product-gallery-lightbox]');
        if (!root || !this.elements.viewer) return;
        const button = root.querySelector('button[data-media-id]');
        if (!button) return;
        const activeSlide =
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        const openerToggle = activeSlide?.querySelector('.product__media-toggle[data-media-id]');
        const id = openerToggle?.getAttribute('data-media-id');
        if (id) button.setAttribute('data-media-id', id);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncGalleryLightboxMediaId();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;
        deferredMedia.loadContent(false, true, true);
        window.setTimeout(() => deferredMedia.loadContent(false, true, true), 320);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
